import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { AppLayout } from "@/components/AppLayout";
import { RoleGuard } from "@/components/RoleGuard";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PageHeader } from "@/components/padroes";
import { useAuth } from "@/context/AuthContext";
import { useAtualizacaoNF, STATUS_AGUARDANDO_OCR } from "@/context/useAtualizacaoNF";
import { useNomeBonificacao } from "@/hooks/useNomeBonificacao";
import { StatusBadge } from "@/components/StatusBadge";
import { CancelarNFModal } from "@/components/CancelarNFModal";
import { api } from "@/lib/api";
import { formatBRL, formatDate, formatMesRef } from "@/lib/format";
import type { NFStatus, NotaFiscalResumo } from "@/data/mock";
import { usePaginado } from "@/hooks/usePaginado";
import { Paginacao } from "@/components/Paginacao";
import { Button } from "@/components/ui/button";
import { FilePlus2, FileUp, Ban, ChevronRight } from "lucide-react";

const ALL_STATUSES: NFStatus[] = [
  "A validar", "Válido", "CNPJ Inválido", "Erro OCR", "Em análise", "Com ressalvas", "Aprovada", "Recusada", "Cancelada",
];

/** GET /dashboard/colaborador — só os contadores do cabeçalho (valores em reais). */
interface ResumoNFs {
  total: number;
  valorAprovado: number;
}

const capitalizar = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

const MinhasNFs = () => {
  const { user } = useAuth();
  const nav = useNavigate();
  const nomeBonificacao = useNomeBonificacao();
  const [statusFilter, setStatusFilter] = useState<NFStatus | "all">("all");
  const [modalNF, setModalNF] = useState<NotaFiscalResumo | null>(null);
  const [resumo, setResumo] = useState<ResumoNFs | null>(null);
  const clt = user?.vinculo === "clt";

  // /nfs/me: só as NFs do próprio usuário, qualquer que seja o perfil.
  const lista = usePaginado<NotaFiscalResumo>("/nfs/me", {
    status: statusFilter === "all" ? "" : statusFilter,
  });
  useAtualizacaoNF(lista.recarregar, lista.items.some((n) => n.status === STATUS_AGUARDANDO_OCR));

  useEffect(() => {
    if (clt) return;
    // Só o resumo do cabeçalho; sem ele o subtítulo some.
    api.get<ResumoNFs>("/dashboard/colaborador").then(setResumo).catch(() => {});
  }, [clt, lista.total]);

  // CLT não emite NF: a página dele é o holerite.
  if (clt) return <Navigate to="/colaborador/demonstrativo" replace />;

  return (
    <RoleGuard role={["colaborador", "rh", "financeiro"]}>
      <AppLayout title="Minhas Notas Fiscais">
        <div className="mx-auto flex max-w-6xl flex-col gap-4">
          <PageHeader
            titulo="Minhas Notas Fiscais"
            subtitulo={
              resumo &&
              `${resumo.total} ${resumo.total === 1 ? "enviada" : "enviadas"} · ${formatBRL(resumo.valorAprovado)} aprovados`
            }
            acoes={
              <>
                <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as NFStatus | "all")}>
                  <SelectTrigger className="h-[38px] w-48 bg-card"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os status</SelectItem>
                    {ALL_STATUSES.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button asChild className="h-[38px]">
                  <Link to="/colaborador/nova"><FilePlus2 /> Enviar nova NF</Link>
                </Button>
              </>
            }
          />

          <section className="overflow-hidden rounded-xl border bg-card">
            <Table className="min-w-[820px]">
              <TableHeader>
                <TableRow>
                  <TableHead className="pl-5">NF</TableHead>
                  <TableHead>Tomador</TableHead>
                  <TableHead>Mês ref.</TableHead>
                  <TableHead className="text-right">Valor</TableHead>
                  <TableHead>Enviada</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-0" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {lista.items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="py-12 text-center text-muted-foreground">
                      {lista.carregando ? "Carregando..." : "Nenhuma NF encontrada"}
                    </TableCell>
                  </TableRow>
                ) : (
                  lista.items.map((n) => (
                    <TableRow
                      key={n.id}
                      className="cursor-pointer"
                      onClick={() => nav(`/colaborador/notas/${n.id}`)}
                    >
                      <TableCell className="pl-5">
                        <div className="flex flex-col gap-0.5">
                          <span className="font-medium">nº {n.numero}</span>
                          <span className="text-xs text-muted-foreground">
                            {n.tipo === "bonificacao" ? nomeBonificacao : "Salário"}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>{n.tomadorNome ?? "—"}</TableCell>
                      <TableCell>{capitalizar(formatMesRef(n.mesReferencia))}</TableCell>
                      <TableCell className="text-right font-semibold">{formatBRL(n.valor)}</TableCell>
                      <TableCell className="text-muted-foreground">{formatDate(n.dataEnvio)}</TableCell>
                      <TableCell><StatusBadge status={n.status} /></TableCell>
                      <TableCell className="pr-3">
                        <div className="flex items-center justify-end gap-2">
                          {n.status === "Recusada" && n.duplicada && (
                            // Cópia de uma NF já enviada: cancelar cancelaria a nota válida.
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-8 border-amber-500/40 text-xs text-amber-700 hover:bg-amber-500/10 hover:text-amber-800 dark:text-amber-400"
                              onClick={(e) => {
                                e.stopPropagation();
                                nav(`/colaborador/nova?substituir=${n.id}`);
                              }}
                            >
                              <FileUp className="!size-3" /> Enviar nota correta
                            </Button>
                          )}
                          {n.status === "Recusada" && !n.duplicada && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-8 border-destructive/40 text-xs text-destructive hover:bg-destructive/10"
                              onClick={(e) => {
                                e.stopPropagation();
                                setModalNF(n);
                              }}
                            >
                              <Ban className="!size-3" /> Enviar cancelamento
                            </Button>
                          )}
                          <ChevronRight className="h-4 w-4 text-muted-foreground/70" aria-hidden />
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </section>

          <Paginacao
            page={lista.page}
            pageSize={lista.pageSize}
            total={lista.total}
            onPage={lista.setPage}
            carregando={lista.carregando}
            rotulo="notas"
          />
        </div>

        {modalNF && (
          <CancelarNFModal
            nf={modalNF}
            open={!!modalNF}
            onClose={() => setModalNF(null)}
            onSuccess={() => {
              setModalNF(null);
              void lista.recarregar();
            }}
          />
        )}
      </AppLayout>
    </RoleGuard>
  );
};

export default MinhasNFs;
